<template>
  <div
    ref="root"
    class="unit-filter"
  >
    <span
      :id="labelId"
      class="unit-filter-label"
    >Visualizar dados de</span>

    <button
      ref="trigger"
      type="button"
      class="unit-filter-trigger"
      :class="{ 'is-open': open }"
      :disabled="disabled || loading"
      :aria-labelledby="labelId"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <span class="unit-filter-badge">
        <i
          class="fas"
          :class="selectedIcon"
          aria-hidden="true"
        />
      </span>

      <span class="unit-filter-value">
        {{ loading ? "Carregando unidades…" : selectedLabel }}
      </span>

      <span
        v-if="selectedMeta"
        class="unit-filter-meta"
      >{{ selectedMeta }}</span>

      <span
        v-if="busy"
        class="loading very-small unit-filter-spinner"
        aria-hidden="true"
      />

      <i
        v-else
        class="fas unit-filter-chevron"
        :class="open ? 'fa-chevron-up' : 'fa-chevron-down'"
        aria-hidden="true"
      />
    </button>

    <transition name="unit-filter-pop">
      <div
        v-if="open"
        class="unit-filter-panel"
        role="listbox"
        :aria-labelledby="labelId"
        @keydown="onPanelKeydown"
      >
        <div class="unit-filter-section">
          <span class="unit-filter-section-title">{{ defaultSectionTitle }}</span>

          <button
            type="button"
            class="unit-filter-option"
            :class="{ 'is-selected': modelValue === null }"
            role="option"
            :aria-selected="modelValue === null ? 'true' : 'false'"
            @click="select(null)"
          >
            <span class="unit-filter-option-label">{{ defaultLabel }}</span>
            <span
              v-if="unitsCountLabel"
              class="unit-filter-option-meta"
            >{{ unitsCountLabel }}</span>
            <i
              v-if="modelValue === null"
              class="fas fa-check unit-filter-check"
              aria-hidden="true"
            />
          </button>
        </div>

        <div
          v-for="section in sections"
          :key="section.title"
          class="unit-filter-section"
        >
          <span class="unit-filter-section-title">{{ section.title }}</span>

          <button
            v-for="unit in section.units"
            :key="unit.id"
            type="button"
            class="unit-filter-option"
            :class="{ 'is-selected': modelValue === unit.id }"
            role="option"
            :aria-selected="modelValue === unit.id ? 'true' : 'false'"
            :title="unit.fullPath"
            @click="select(unit.id)"
          >
            <span class="unit-filter-option-label">{{ unit.name }}</span>
            <span
              v-if="unit.parentPath"
              class="unit-filter-option-meta"
            >{{ unit.parentPath }}</span>
            <i
              v-if="modelValue === unit.id"
              class="fas fa-check unit-filter-check"
              aria-hidden="true"
            />
          </button>
        </div>

        <span
          v-if="options.length === 0"
          class="unit-filter-empty"
        >Nenhuma outra unidade disponível.</span>
      </div>
    </transition>
  </div>
</template>

<script>
let instanceCount = 0;

export default {
  name: "UnitFilterSelect",
  props: {
    modelValue: {
      type: [Number, null],
      default: null
    },
    options: {
      required: true,
      type: Array
    },
    defaultLabel: {
      required: true,
      type: String
    },
    canSelectInstitution: {
      required: false,
      type: Boolean,
      default: false
    },
    loading: {
      required: false,
      type: Boolean,
      default: false
    },
    busy: {
      required: false,
      type: Boolean,
      default: false
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue"],
  data() {
    instanceCount += 1;

    return {
      open: false,
      labelId: `unit-filter-label-${instanceCount}`
    };
  },
  computed: {
    selectedUnit() {
      return this.options.find((unit) => unit.id === this.modelValue) || null;
    },
    selectedLabel() {
      return this.selectedUnit ? this.selectedUnit.name : this.defaultLabel;
    },
    selectedMeta() {
      if (this.loading) return "";

      return this.selectedUnit
        ? this.selectedUnit.parentPath
        : this.unitsCountLabel;
    },
    selectedIcon() {
      if (!this.selectedUnit) return "fa-hospital";

      return this.selectedUnit.isTeam ? "fa-users" : "fa-sitemap";
    },
    unitsCountLabel() {
      const total = this.options.length;

      if (total === 0) return "";

      return total === 1 ? "1 unidade" : `${total} unidades`;
    },
    defaultSectionTitle() {
      return this.canSelectInstitution ? "Instituição" : "Meu escopo";
    },
    sections() {
      const units = this.options.filter((unit) => !unit.isTeam);
      const teams = this.options.filter((unit) => unit.isTeam);
      const sections = [];

      if (units.length > 0) sections.push({ title: "Unidades", units });
      if (teams.length > 0) sections.push({ title: "Equipes", units: teams });

      return sections;
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    toggle() {
      this.open = !this.open;

      if (this.open) this.focusSelectedOption();
    },
    close(returnFocus = false) {
      this.open = false;

      if (returnFocus && this.$refs.trigger) this.$refs.trigger.focus();
    },
    select(unitId) {
      this.close(true);

      if (unitId !== this.modelValue) this.$emit("update:modelValue", unitId);
    },
    handleClickOutside(event) {
      if (!this.open) return;
      if (this.$refs.root && this.$refs.root.contains(event.target)) return;

      this.close();
    },
    onTriggerKeydown(event) {
      if (event.key === "Escape") {
        this.close();
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();

        if (!this.open) this.toggle();
        else this.focusSelectedOption();
      }
    },
    onPanelKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close(true);
        return;
      }

      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

      event.preventDefault();

      const items = this.optionElements();
      const current = items.indexOf(document.activeElement);
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = (current + step + items.length) % items.length;

      if (items[next]) items[next].focus();
    },
    optionElements() {
      if (!this.$refs.root) return [];

      return Array.from(
        this.$refs.root.querySelectorAll(".unit-filter-option")
      );
    },
    focusSelectedOption() {
      this.$nextTick(() => {
        const items = this.optionElements();
        const selected = items.find((item) =>
          item.classList.contains("is-selected")
        );

        if (selected) selected.focus();
        else if (items[0]) items[0].focus();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$uf-purple: #9a397d;
$uf-dark: #2d2251;
$uf-muted: #9b9aa8;
$uf-border: #ebe9f2;

.unit-filter {
  position: relative;
  display: inline-block;
  min-width: 300px;
  max-width: 380px;
}

.unit-filter-label {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  line-height: 15px;
  font-weight: 600;
  color: $uf-muted;
}

.unit-filter-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px 14px 6px 6px;
  cursor: pointer;
  border: 1px solid $uf-border;
  border-radius: 26px;
  background-color: #ffffff;
  box-shadow: 0 1px 4px 0 rgba(45, 34, 81, 0.08);
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:hover:not(:disabled),
  &.is-open {
    border-color: rgba(154, 57, 125, 0.35);
    box-shadow: 0 3px 10px 0 rgba(45, 34, 81, 0.12);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.unit-filter-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 12px;
  color: $uf-dark;
  background-color: #f4f2f8;
}

.unit-filter-value {
  flex: 1 1 auto;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  line-height: 19px;
  font-weight: 500;
  color: $uf-dark;
}

.unit-filter-meta {
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 15px;
  color: $uf-muted;
}

.unit-filter-chevron {
  flex: 0 0 auto;
  font-size: 10px;
  color: $uf-dark;
}

.unit-filter-spinner {
  flex: 0 0 15px;
  height: 15px;
}

.unit-filter-panel {
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px 0 10px;
  border: 1px solid $uf-border;
  border-radius: 14px;
  background-color: #ffffff;
  box-shadow: 0 6px 20px 0 rgba(45, 34, 81, 0.14);
}

.unit-filter-section-title {
  display: block;
  padding: 10px 18px 4px;
  font-size: 10px;
  line-height: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $uf-muted;
}

.unit-filter-option {
  display: flex;
  align-items: baseline;
  gap: 10px;
  width: 100%;
  padding: 7px 18px;
  cursor: pointer;
  border: none;
  text-align: left;
  background: none;

  &:hover,
  &:focus {
    outline: none;
    background-color: #faf7fb;
  }

  &.is-selected .unit-filter-option-label {
    font-weight: 600;
  }
}

.unit-filter-option-label {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  line-height: 18px;
  color: $uf-purple;
}

.unit-filter-option-meta {
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 15px;
  color: $uf-muted;
}

.unit-filter-check {
  flex: 0 0 auto;
  font-size: 11px;
  color: $uf-purple;
}

.unit-filter-empty {
  display: block;
  padding: 8px 18px;
  font-size: 12px;
  color: $uf-muted;
}

.unit-filter-pop-enter-active,
.unit-filter-pop-leave-active {
  transition: opacity 0.15s ease-in-out, transform 0.15s ease-in-out;
}

.unit-filter-pop-enter-from,
.unit-filter-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 768px) {
  .unit-filter {
    display: block;
    min-width: 100%;
    max-width: 100%;
  }
}
</style>
